import { NavLink } from "react-router-dom";

import { LastUpdatedLabel } from "../components/LastUpdatedLabel";
import { MemoReader } from "../components/MemoReader";
import { SummaryCard } from "../components/SummaryCard";
import { PageContainer } from "../layout/PageContainer";
import { useContent } from "../hooks/useContent";
import { api } from "../lib/api";

export function MemoPage() {
  const { data, loading, error } = useContent("memo-page", async () => {
    const [memoSummary, markdown, status] = await Promise.all([api.getMemoSummary(), api.getMemo(), api.getStatus()]);
    return { memoSummary, markdown, status };
  });

  return (
    <PageContainer
      eyebrow="Supporting Detail - Longform Memo"
      title="Longform memo supporting the investor surfaces"
      lede="This route renders the canonical published memo as supporting longform context. It is not a primary investor surface and should be read alongside the rebased top-level pages."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading memo...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? (
        <div className="memo-layout">
          <aside className="memo-sidebar stack-lg">
            <SummaryCard eyebrow="Memo Snapshot" title={data.memoSummary.recommendation} description={data.memoSummary.summary} footer={<LastUpdatedLabel value={data.status.lastPublishedAt} />}>
              <div className="stack-sm">
                <div>
                  <span className="card-eyebrow">Lead Concept</span>
                  <div className="card-copy strong-copy">{data.memoSummary.leadConcept}</div>
                </div>
                <div>
                  <span className="card-eyebrow">Fallback Concept</span>
                  <div className="card-copy">{data.memoSummary.fallbackConcept}</div>
                </div>
              </div>
            </SummaryCard>
            <SummaryCard eyebrow="Evidence" title="Methodology and sources" description="The public site keeps the raw source-log batches private and exposes a curated evidence summary instead.">
              <div className="row-wrap gap-sm overview-go-deeper-actions">
                <NavLink to="/sources-methodology" className="link-button link-button-secondary">Open Sources & Methodology</NavLink>
              </div>
            </SummaryCard>
            <SummaryCard
              eyebrow="Parent Surfaces"
              title="Return to the primary investor surfaces before using the memo as the main read"
              description="The memo remains useful as supporting longform context. The canonical investor-facing posture now lives in the rebased top-level surfaces."
            >
              <div className="row-wrap gap-sm overview-go-deeper-actions">
                <NavLink to="/investment-case" className="link-button link-button-secondary">Investment Case</NavLink>
                <NavLink to="/financials" className="link-button link-button-secondary">Financials</NavLink>
                <NavLink to="/sources-methodology" className="link-button link-button-secondary">Sources & Methodology</NavLink>
              </div>
            </SummaryCard>
            <SummaryCard eyebrow="Strengths" title="Keep" description="These points currently support continuation.">
              <ul className="bullet-list">
                {data.memoSummary.strengths.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard eyebrow="Cautions" title="Watch" description="These still control the recommendation posture.">
              <ul className="bullet-list">
                {data.memoSummary.cautions.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </aside>
          <div className="memo-main stack-lg">
            <MemoReader markdown={data.markdown} />
          </div>
        </div>
      ) : null}
    </PageContainer>
  );
}
